/**
 * An app's modal (Slack's modals; core AppModalViewModel): the top view of the stack in a `Sheet` (Radix Dialog:
 * focus trap, Escape, focus back to where it was, `aria-modal`). Title, a Back button while views are stacked, the
 * blocks (inputs with label, "(optional)", hint and errors, every element), then the close button ("Cancel" or the
 * app's label: back one view) and the submit button. X, Escape and the overlay close the whole modal.
 */
import type { AppModalState, AppModalViewModel, RichTextContext } from "@bundle/core";
import { useT, useTr } from "../i18n/I18nProvider.tsx";
import { Banner, Button, IconButton, Sheet, Stack } from "../primitives/index.ts";
import { BlockKit } from "./BlockKit.tsx";

export type AppModalProps = {
  vm: AppModalViewModel;
  state: AppModalState;
  ctx: RichTextContext;
  /** Opens a button's link (the platform's opener). */
  openUrl?: (url: string) => void;
  testID: string;
};

export function AppModal({ vm, state, ctx, openUrl, testID }: AppModalProps) {
  const t = useT();
  const tr = useTr();
  const stack = state.stack;
  const top = stack?.views.at(-1);
  const errors: Record<string, string> = {};
  for (const [blockId, d] of Object.entries(state.errors)) errors[blockId] = tr(d);
  const stacked = (stack?.views.length ?? 0) > 1;
  return (
    <Sheet
      open={top !== undefined}
      onClose={() => vm.close(true)}
      title={top?.view.title.text ?? ""}
      headerActions={stacked ? <IconButton icon="arrowLeft" size="sm" accessibilityLabel={t("appModal.back")} onPress={() => vm.close(false)} testID={`${testID}-back`} /> : undefined}
      testID={testID}
    >
      {top === undefined ? null : (
        <Stack gap="4" testID={`${testID}-view-${top.id}`}>
          {state.notice === undefined ? null : <Banner tone="danger" message={tr(state.notice)} onDismiss={() => vm.clearNotice()} testID={`${testID}-notice`} />}
          {/* Keyed by view id: a pushed or replaced view starts fresh (focus_on_load runs again). */}
          <BlockKit
            key={top.id}
            blocks={top.view.blocks}
            ctx={ctx}
            values={state.values}
            errors={errors}
            channelId={top.channelId}
            container={{ viewId: top.id }}
            onValue={(blockId, actionId, value) => vm.setValue(blockId, actionId, value)}
            onButton={(blockId, button) => vm.press(blockId, button, openUrl)}
            onPick={(blockId, menu, value) => vm.pickOverflow(blockId, menu, value)}
            onEnter={(blockId, actionId) => vm.dispatchText(blockId, actionId)}
            testID={`${testID}-blocks`}
          />
          <Stack direction="horizontal" gap="2" justify="end">
            <Button label={top.view.close?.text ?? t("appModal.cancel")} variant="secondary" onPress={() => vm.close(false)} testID={`${testID}-cancel`} />
            {top.view.submit === undefined ? null : (
              <Button
                label={top.view.submit.text}
                variant="primary"
                loading={state.submitting}
                disabled={top.view.submit_disabled === true}
                onPress={() => vm.submit()}
                testID={`${testID}-submit`}
              />
            )}
          </Stack>
        </Stack>
      )}
    </Sheet>
  );
}
